/**
 * PATH: packages/job-hub/src/jobs/tabs/job-qa/useQaHistory.ts
 * FILE_ID: JOBHUB-QA-HISTORY-001
 * PURPOSE: QA-wide undo/redo stack owned by the tab, with Cmd/Ctrl+Z.
 * LANDMINE: preventDefault only when the stack will act, so empty history
 *      leaves native text undo alone. Clears on inspection change.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import {
  createUndoStack,
  isTextInputTarget,
  type UndoEntry,
  type UndoResult,
} from '@quotech/ui'

export function useQaHistory(inspectionId: string | null) {
  const stackRef = useRef(createUndoStack())
  const [lastLabel, setLastLabel] = useState<string | null>(null)
  const [, bump] = useState(0)
  const refresh = useCallback(() => bump((count) => count + 1), [])

  useEffect(() => {
    stackRef.current.clear()
    setLastLabel(null)
    refresh()
  }, [inspectionId, refresh])

  const register = useCallback((entry: UndoEntry) => {
    stackRef.current.push(entry)
    setLastLabel(entry.label)
    refresh()
  }, [refresh])

  const undo = useCallback(async (): Promise<UndoResult> => {
    const result = await stackRef.current.undo()
    refresh()
    return result
  }, [refresh])

  const redo = useCallback(async (): Promise<UndoResult> => {
    const result = await stackRef.current.redo()
    refresh()
    return result
  }, [refresh])

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (!(event.metaKey || event.ctrlKey) || event.altKey) return
      if (event.key.toLowerCase() !== 'z') return
      if (isTextInputTarget(event.target)) return
      if (event.shiftKey) {
        if (!stackRef.current.canRedo()) return
        event.preventDefault()
        void redo()
        return
      }
      if (!stackRef.current.canUndo()) return
      event.preventDefault()
      void undo()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [undo, redo])

  return {
    register,
    undo,
    redo,
    canUndo: stackRef.current.canUndo(),
    canRedo: stackRef.current.canRedo(),
    lastLabel,
  }
}

export type QaHistory = ReturnType<typeof useQaHistory>
